import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useT, useLang } from "@/i18n";
import { UserAvatar, PRESET_AVATARS } from "@/components/UserAvatar";
import { AvatarPickerTile, AvatarPreviewModal } from "@/components/AvatarPreview";

import { FlagRO, FlagUS } from "@/components/CountryFlag";
import { Languages, Mail, Lock, User as UserIcon, MailCheck, RefreshCw, ArrowLeft, Eye, EyeOff, ChevronsDown } from "lucide-react";
import { toast } from "sonner";
import logo from "@/assets/logo.png";
import { LegalFooter } from "@/components/LegalFooter";
import { LegalModal } from "@/components/LegalModal";

export const Route = createFileRoute("/signup")({
  component: SignupPage,
});

function SignupPage() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const t = useT();
  const lang = useLang((s) => s.lang);
  const setLang = useLang((s) => s.setLang);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [username, setUsername] = useState("");
  const [avatar, setAvatar] = useState(PRESET_AVATARS[0]);
  const [submitting, setSubmitting] = useState(false);
  const [verifyEmail, setVerifyEmail] = useState<string | null>(null);
  const [termsAcceptedAt, setTermsAcceptedAt] = useState<string | null>(null);
  const [legalModal, setLegalModal] = useState<"terms" | "privacy" | null>(null);
  const [previewIndex, setPreviewIndex] = useState<number | null>(null);

  useEffect(() => {
    if (!loading && user && !verifyEmail) {
      const pending = (() => {
        try {
          return localStorage.getItem("pendingLeagueJoin");
        } catch {
          return null;
        }
      })();
      if (pending) {
        navigate({ to: "/join/$leagueCode", params: { leagueCode: pending } });
      } else {
        navigate({ to: "/predictions" });
      }
    }
  }, [loading, user, navigate, verifyEmail]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!email.trim() || password.length < 6 || !username.trim()) {
      toast.error(t("Fill all fields (password ≥ 6 chars)", "Completați toate câmpurile (parolă ≥ 6 caractere)"));
      return;
    }
    if (!termsAcceptedAt) {
      toast.error(t("You must accept the Terms and Privacy Policy", "Trebuie să accepți Termenii și Politica de Confidențialitate"));
      return;
    }
    setSubmitting(true);
    const trimmedEmail = email.trim();
    const pendingCode = (() => {
      try {
        return localStorage.getItem("pendingLeagueJoin");
      } catch {
        return null;
      }
    })();
    const redirectTo = pendingCode
      ? `${window.location.origin}/join/${pendingCode}`
      : `${window.location.origin}/login`;
    const { error } = await supabase.auth.signUp({
      email: trimmedEmail,
      password,
      options: {
        emailRedirectTo: redirectTo,
        data: {
          username: username.trim(),
          avatar,
          language: lang,
          terms_accepted_at: termsAcceptedAt,
        },
      },
    });
    setSubmitting(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    // Always show the verification screen. Even if Supabase returned a
    // session, the user still needs to click the link to confirm the
    // address. Signing out keeps the gate honest.
    await supabase.auth.signOut();
    setVerifyEmail(trimmedEmail);
  }

  if (verifyEmail) {
    return (
      <VerifyEmailScreen
        email={verifyEmail}
        onChangeEmail={() => {
          setVerifyEmail(null);
          setPassword("");
        }}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-accent">
      <div className="mx-auto flex min-h-screen max-w-2xl flex-col px-4 pt-2 pb-6">
        <div className="mb-2 text-center">
          <img src={logo} alt="Predictor Pro" className="mx-auto h-44 w-44 object-contain drop-shadow-2xl" />
          <h1 className="-mt-2 text-3xl font-bold tracking-tight">Predictor Pro</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {t("Create your account to start predicting.", "Creează-ți contul pentru a începe.")}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="rounded-2xl border bg-card p-6 shadow-sm">
          <Field icon={<Mail className="h-4 w-4" />} label={t("Email", "Email")}>
            <input type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} required />
          </Field>
          <Field icon={<Lock className="h-4 w-4" />} label={t("Password", "Parolă")}>
            <div className="relative mt-1">
              <input type={showPassword ? "text" : "password"} autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} className="w-full rounded-lg border border-input bg-background px-3 py-2.5 pr-11 text-base focus:outline-none focus:ring-2 focus:ring-ring" required minLength={6} />
              <button
                type="button"
                onClick={() => setShowPassword((s) => !s)}
                className="absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer text-muted-foreground transition-colors hover:text-foreground"
                aria-label={showPassword ? t("Hide password", "Ascunde parola") : t("Show password", "Arată parola")}
              >
                {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>
          </Field>
          <Field icon={<UserIcon className="h-4 w-4" />} label={t("Nickname", "Porecla")}>
            <input value={username} onChange={(e) => setUsername(e.target.value)} className={inputCls} maxLength={20} placeholder={t("e.g. CarpathianWolf", "ex. LupulCarpaților")} required />
            <p className="mt-1 text-xs text-muted-foreground">
              {t("This is the name other players will see on leaderboards.", "Aceasta este porecla pe care o vor vedea ceilalți jucători în clasamente.")}
            </p>
          </Field>

          <div className="mt-5">
            <div className="mb-2 flex items-center gap-2">
              <UserAvatar avatar={avatar} username={username || "You"} size={40} rounded="rounded-full" />
              <div className="flex items-center gap-1.5 text-sm font-medium">
                <ChevronsDown className="h-3.5 w-3.5 text-muted-foreground" />
                {t("Pick your avatar", "Alege avatarul")}
              </div>
            </div>
            <div className="max-h-[280px] overflow-y-auto show-scrollbar pr-1">
              <div className="grid grid-cols-4 gap-3">
                {PRESET_AVATARS.map((a, i) => (
                  <AvatarPickerTile
                    key={a}
                    avatar={a}
                    selected={avatar === a}
                    onSelect={() => setAvatar(a)}
                    onPreview={() => setPreviewIndex(i)}
                  />
                ))}
              </div>
            </div>
          </div>

          <div className="mt-5">
            <div className="mb-2 flex items-center gap-2 text-sm font-medium">
              <Languages className="h-4 w-4 text-primary" />
              {t("Language", "Limbă")}
            </div>
            <div className="grid grid-cols-2 gap-2">
              {([
                { code: "en" as const, label: "English", Flag: FlagUS },
                { code: "ro" as const, label: "Română", Flag: FlagRO },
              ]).map((opt) => (
                <button
                  key={opt.code}
                  type="button"
                  onClick={() => setLang(opt.code)}
                  className={`flex items-center justify-center gap-2 rounded-lg border px-3 py-2.5 text-sm font-semibold transition-colors ${
                    lang === opt.code
                      ? "border-primary bg-primary text-primary-foreground"
                      : "border-input bg-background text-muted-foreground hover:bg-accent hover:text-foreground"
                  }`}
                >
                  <opt.Flag className="h-4 w-6 rounded-sm shadow-sm" />
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          <label className="mt-6 flex cursor-pointer items-start gap-2.5 rounded-lg border border-input bg-background/50 p-3 text-sm">
            <input
              type="checkbox"
              checked={!!termsAcceptedAt}
              onChange={(e) => setTermsAcceptedAt(e.target.checked ? new Date().toISOString() : null)}
              className="mt-0.5 h-4 w-4 cursor-pointer accent-primary"
            />
            <span className="text-muted-foreground">
              {lang === "ro" ? (
                <>
                  Sunt de acord cu{" "}
                  <button type="button" onClick={() => setLegalModal("terms")} className="font-semibold text-primary hover:underline">Termenii și Condițiile</button>
                  {" "}și{" "}
                  <button type="button" onClick={() => setLegalModal("privacy")} className="font-semibold text-primary hover:underline">Politica de Confidențialitate</button>
                </>
              ) : (
                <>
                  I agree to the{" "}
                  <button type="button" onClick={() => setLegalModal("terms")} className="font-semibold text-primary hover:underline">Terms and Conditions</button>
                  {" "}and{" "}
                  <button type="button" onClick={() => setLegalModal("privacy")} className="font-semibold text-primary hover:underline">Privacy Policy</button>
                </>
              )}
            </span>
          </label>

          <button type="submit" disabled={submitting || !termsAcceptedAt} className="mt-4 w-full rounded-lg bg-primary py-3 text-base font-semibold text-primary-foreground transition-opacity hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed">
            {submitting ? t("Creating…", "Se creează…") : t("Create account", "Creează cont")}
          </button>

          <p className="mt-4 text-center text-sm text-muted-foreground">
            {t("Already have an account?", "Ai deja cont?")}{" "}
            <Link to="/login" className="font-semibold text-primary hover:underline">
              {t("Sign in", "Autentificare")}
            </Link>
          </p>
          <LegalFooter className="mt-4" />
        </form>
      </div>
      {legalModal && <LegalModal doc={legalModal} onClose={() => setLegalModal(null)} />}
      <AvatarPreviewModal
        open={previewIndex !== null}
        avatars={PRESET_AVATARS}
        index={previewIndex ?? 0}
        onIndexChange={(i) => setPreviewIndex(i)}
        onSelect={(a) => setAvatar(a)}
        onClose={() => setPreviewIndex(null)}
      />
    </div>
  );
}

function VerifyEmailScreen({ email, onChangeEmail }: { email: string; onChangeEmail: () => void }) {
  const t = useT();
  const [resending, setResending] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (cooldown <= 0) return;
    const id = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(id);
  }, [cooldown]);

  async function handleResend() {
    setResending(true);
    const { error } = await supabase.auth.resend({
      type: "signup",
      email,
      options: { emailRedirectTo: `${window.location.origin}/login` },
    });
    setResending(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success(t("Verification email sent", "Email de verificare trimis"));
    setCooldown(30);
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-accent">
      <div className="mx-auto flex min-h-screen max-w-md flex-col px-4 pt-2 pb-6">
        <div className="mb-2 text-center">
          <img src={logo} alt="Predictor Pro" className="mx-auto h-44 w-44 object-contain drop-shadow-2xl" />
        </div>

        <div className="rounded-2xl border bg-card p-6 shadow-sm text-center">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-primary/10">
            <MailCheck className="h-7 w-7 text-primary" />
          </div>
          <h1 className="text-2xl font-bold tracking-tight">
            {t("Check your inbox!", "Verifică-ți emailul!")}
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            {t(
              "We've sent a verification link to",
              "Am trimis un link de verificare la"
            )}
          </p>
          <p className="mt-1 break-all text-base font-semibold">{email}</p>
          <p className="mt-3 text-sm text-muted-foreground">
            {t(
              "Click the link in the email to activate your account, then sign in.",
              "Apasă pe link-ul din email pentru a-ți activa contul, apoi autentifică-te."
            )}
          </p>

          <button
            type="button"
            onClick={handleResend}
            disabled={resending || cooldown > 0}
            className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-primary py-3 text-base font-semibold text-primary-foreground transition-opacity hover:opacity-90 disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${resending ? "animate-spin" : ""}`} />
            {cooldown > 0
              ? t(`Resend in ${cooldown}s`, `Retrimite în ${cooldown}s`)
              : resending
                ? t("Sending…", "Se trimite…")
                : t("Resend verification email", "Retrimite emailul de verificare")}
          </button>

          <Link
            to="/login"
            className="mt-3 inline-flex w-full items-center justify-center rounded-lg border border-input bg-background py-3 text-sm font-semibold hover:bg-accent"
          >
            {t("I've verified — sign in", "Am verificat — autentificare")}
          </Link>

          <button
            type="button"
            onClick={onChangeEmail}
            className="mt-4 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-primary hover:underline"
          >
            <ArrowLeft className="h-3 w-3" />
            {t("Wrong email? Go back and sign up again", "Email greșit? Înapoi pentru a te înregistra din nou")}
          </button>
        </div>
      </div>
    </div>
  );
}

const inputCls = "mt-1 w-full rounded-lg border border-input bg-background px-3 py-2.5 text-base focus:outline-none focus:ring-2 focus:ring-ring";

function Field({ label, icon, children }: { label: string; icon?: React.ReactNode; children: React.ReactNode }) {
  return (
    <label className="mt-4 block first:mt-0">
      <span className="flex items-center gap-1.5 text-sm font-medium">
        {icon}
        {label}
      </span>
      {children}
    </label>
  );
}
